import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Search, ClipboardCheck, Timer } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";

const SITE = "https://oceandata.ca";

const OBLIGATIONS = [
  {
    title: "Consentement clair",
    body: "Vous devez savoir pourquoi vous détenez chaque renseignement personnel — et pouvoir le démontrer. Difficile quand la même donnée vit dans six endroits sans historique.",
  },
  {
    title: "Accès, correction, suppression",
    body: "Un client peut demander de voir, corriger ou effacer ses renseignements. Si la réponse exige de fouiller Excel, les courriels et trois logiciels, chaque demande devient un projet.",
  },
  {
    title: "Signalement d'incidents",
    body: "Une fuite de données doit être évaluée et, dans certains cas, signalée. Encore faut-il savoir quelles données ont été touchées — et où elles étaient.",
  },
  {
    title: "Un responsable désigné",
    body: "Par défaut, c'est le dirigeant. Concrètement : quelqu'un doit pouvoir répondre « voici où sont nos données, et voici qui y a touché » — sans paniquer.",
  },
];

const FAQ = [
  {
    q: "Est-ce que la Loi 25 s'applique à ma PME ?",
    a: "Oui. Dès que votre entreprise détient des renseignements personnels — clients, employés, fournisseurs — la Loi 25 s'applique, peu importe la taille. Les obligations s'échelonnent, mais aucune PME québécoise n'en est exempte.",
  },
  {
    q: "Est-ce qu'Ocean Data donne des avis juridiques ?",
    a: "Non — et cette page n'en est pas un. Nous sommes des spécialistes des processus, pas des avocats. Ce que nous faisons : rendre vos données trouvables, traçables et gouvernables, pour que votre conformité repose sur des systèmes plutôt que sur la mémoire.",
  },
  {
    q: "Par où commencer concrètement ?",
    a: "Par une revue gratuite de 15 minutes. On cartographie où vivent vos données clients aujourd'hui — courriels, chiffriers, CRM, comptabilité — et on vous montre ce qu'un dossier unique et connecté changerait. Pour les obligations précises, on vous dirigera vers un conseiller juridique si nécessaire.",
  },
];

export default function Loi25Page() {
  const { setOpen } = useSimplify();

  useEffect(() => {
    document.title = "Loi 25 et vos données clients | Ocean Data — Montréal";
    document.documentElement.lang = "fr";
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = "fr-CA";
    el.href = `${SITE}/fr/loi-25-donnees-clients`;
    el.setAttribute("data-hreflang-injected", "true");
    document.head.appendChild(el);
    return () => {
      document.documentElement.lang = "en";
      el.remove();
    };
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE}/fr/loi-25-donnees-clients`,
        name: "Loi 25 et vos données clients",
        description:
          "La Loi 25 ne demande pas plus de logiciels — elle demande de savoir où sont vos données clients et de pouvoir le prouver. Comment un dossier unique et connecté simplifie la conformité pour les PME du Québec.",
        inLanguage: "fr-CA",
        isPartOf: { "@id": `${SITE}/#website` },
        about: { "@id": `${SITE}/#org` },
      },
      {
        "@type": "FAQPage",
        inLanguage: "fr-CA",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang="fr">
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            Montréal · Loi 25 & simplification
          </p>
          <h1
            data-testid="loi25-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="loi25-headline-line-1">Vos données clients sont partout.</MaskedLine>
            <MaskedLine delay={0.3} testId="loi25-headline-line-2">
              <span className="text-brand-teal">C'est ça, le vrai risque.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            La Loi 25 ne demande pas plus de logiciels. Elle demande de savoir
            où sont vos données — et de pouvoir le prouver. C'est exactement ce
            qu'un dossier unique et connecté rend simple.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <button
              data-testid="loi25-cta-hero"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              Montrez-moi où sont mes données
              <ArrowRight size={18} />
            </button>
            <p className="text-sm text-slate-500">Revue gratuite de 15 minutes · Sans engagement</p>
          </motion.div>
        </div>
      </section>

      {/* Ce que la Loi 25 demande */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            En langage simple
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Ce que la Loi 25 demande, concrètement
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
            {OBLIGATIONS.map((o, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div
                  data-testid={`loi25-obligation-${i}`}
                  className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-7 sm:p-8 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-9 h-9 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center">
                      <ShieldCheck size={16} />
                    </span>
                    <h3 className="font-display font-semibold text-lg text-brand-navy leading-snug">{o.title}</h3>
                  </div>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{o.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Le test de 10 minutes */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Le test de 10 minutes
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-8">
            Une question à vous poser aujourd'hui
          </h2>
          <Reveal>
            <div data-testid="loi25-test-panel" className="rounded-3xl bg-white border border-slate-200 p-7 sm:p-10">
              <div className="flex items-start gap-4 mb-6">
                <span className="w-10 h-10 rounded-full bg-brand-goldsoft text-brand-golddeep flex items-center justify-center shrink-0">
                  <Timer size={18} />
                </span>
                <p className="font-display font-medium text-xl text-brand-navy leading-snug">
                  Si un client demande demain la suppression de ses renseignements personnels, dans combien d'endroits devez-vous chercher ?
                </p>
              </div>
              <div className="space-y-3 text-sm sm:text-base text-slate-600 leading-relaxed">
                <p className="flex items-start gap-3"><Search size={16} className="text-brand-teal shrink-0 mt-1" /> Les courriels — boîtes individuelles, pièces jointes, archives.</p>
                <p className="flex items-start gap-3"><Search size={16} className="text-brand-teal shrink-0 mt-1" /> Les chiffriers Excel — et leurs copies, sur les postes et dans les nuages.</p>
                <p className="flex items-start gap-3"><Search size={16} className="text-brand-teal shrink-0 mt-1" /> Le CRM, la comptabilité, les textos, les clés USB du bureau.</p>
              </div>
              <p className="mt-6 text-sm sm:text-base text-slate-600 leading-relaxed border-t border-slate-100 pt-6">
                Chaque réponse de trop, c'est du risque en plus — et du temps en plus à chaque demande. La conformité n'est pas un projet de logiciels. C'est d'abord une question de savoir où les choses vivent.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Un dossier connecté change tout */}
      <section className="py-16 lg:py-24 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold mb-5">
            La version connectée
          </p>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl max-w-2xl mb-14">
            Un seul dossier. Une recherche. Une preuve.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
            {[
              ["Une seule recherche", "Quand les outils que vous utilisez déjà sont connectés, un client = un dossier. Une demande d'accès ou de suppression se traite à un seul endroit, pas dans six."],
              ["Une piste d'audit", "Chaque consultation et chaque changement laissent une trace. « Qui a touché quoi, quand » arrête d'être un mystère — c'est écrit."],
              ["La diligence, prouvée", "En cas de question de la CAI, vous ne racontez pas vos efforts. Vous les montrez. Les systèmes connectés sont votre preuve de diligence raisonnable."],
            ].map(([title, body], i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`loi25-benefit-${i}`} className="rounded-2xl bg-brand-navysurf border border-white/10 p-7 sm:p-8 h-full">
                  <ClipboardCheck size={18} className="text-brand-gold mb-4" />
                  <h3 className="font-display font-semibold text-xl text-white mb-3">{title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-10 text-sm text-slate-400 max-w-2xl">
              Ocean Data n'est pas un cabinet d'avocats et cette page n'est pas un avis juridique. Nous rendons vos données trouvables et traçables; pour vos obligations précises, consultez un conseiller juridique.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Questions fréquentes
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-12">
            Ce que les dirigeants nous demandent
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <details data-testid={`loi25-faq-${i}`} className="group rounded-2xl bg-white border border-slate-200 px-7 py-6 open:border-brand-teal transition-colors">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-display font-semibold text-lg text-brand-navy">
                    {f.q}
                    <span className="text-brand-teal text-xl transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="rounded-3xl bg-brand-navy text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="max-w-xl">
                <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mb-4">
                  Quinze minutes pour savoir où vivent vos données.
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  On cartographie vos outils actuels, on repère les doubles, et
                  on vous montre ce qu'un dossier unique changerait — pour la
                  conformité, et pour vos semaines.
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 shrink-0">
                <button
                  data-testid="loi25-cta-final"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
                >
                  Demander ma revue gratuite
                  <ArrowRight size={18} />
                </button>
                <p className="text-xs text-slate-400">
                  Aussi utile sans enjeu Loi 25 :{" "}
                  <Link to="/fr/gardez-vos-outils" className="text-brand-gold hover:text-white transition-colors">
                    gardez vos outils, on les connecte
                  </Link>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
